/** Built-in quick-select presets, kept pure so a custom slot can reuse them. */

import {
  addDays,
  addMonths,
  endOfMonth,
  endOfWeek,
  formatHm,
  startOfMonth,
  startOfWeek,
  today,
  type DateRangeValue,
  type WeekStart,
} from "./dateCore";
import type {
  DateTimePickerPreset,
  DateTimePickerPresetName,
  DateTimePickerVariant,
  InternalValue,
} from "./types";

export const PRESET_LABEL: Record<DateTimePickerPresetName, string> = {
  today: "Today",
  yesterday: "Yesterday",
  tomorrow: "Tomorrow",
  thisWeek: "This Week",
  lastWeek: "Last Week",
  thisMonth: "This Month",
  lastMonth: "Last Month",
  thisYear: "This Year",
  last7: "Last 7 Days",
  last30: "Last 30 Days",
  last90: "Last 90 Days",
  next7: "Next 7 Days",
  next30: "Next 30 Days",
};

export function isPresetName(preset: DateTimePickerPreset): preset is DateTimePickerPresetName {
  return typeof preset === "string";
}

export function getPresetLabel(preset: DateTimePickerPreset): string {
  return isPresetName(preset) ? PRESET_LABEL[preset] : preset.label;
}

/** Every preset's natural span. Single-value variants take the `from` end. */
function presetRange(name: DateTimePickerPresetName, weekStartsOn: WeekStart): DateRangeValue {
  const now = today();

  switch (name) {
    case "today":
      return { from: now, to: now };
    case "yesterday": {
      const day = addDays(now, -1);
      return { from: day, to: day };
    }
    case "tomorrow": {
      const day = addDays(now, 1);
      return { from: day, to: day };
    }
    case "thisWeek":
      return { from: startOfWeek(now, weekStartsOn), to: endOfWeek(now, weekStartsOn) };
    case "lastWeek": {
      const anchor = addDays(now, -7);
      return { from: startOfWeek(anchor, weekStartsOn), to: endOfWeek(anchor, weekStartsOn) };
    }
    case "thisMonth":
      return { from: startOfMonth(now), to: endOfMonth(now) };
    case "lastMonth": {
      const anchor = addMonths(now, -1);
      return { from: startOfMonth(anchor), to: endOfMonth(anchor) };
    }
    case "thisYear":
      return { from: new Date(now.getFullYear(), 0, 1), to: new Date(now.getFullYear(), 11, 31) };
    case "last7":
      return { from: addDays(now, -6), to: now };
    case "last30":
      return { from: addDays(now, -29), to: now };
    case "last90":
      return { from: addDays(now, -89), to: now };
    case "next7":
      return { from: now, to: addDays(now, 6) };
    case "next30":
      return { from: now, to: addDays(now, 29) };
    default:
      return { from: now, to: now };
  }
}

/** Resolve a preset for the active variant; single-value ones take the span start. */
export function resolvePreset(
  preset: DateTimePickerPreset,
  variant: DateTimePickerVariant,
  weekStartsOn: WeekStart = 0,
): InternalValue {
  if (!isPresetName(preset)) return preset.getValue(variant);

  const range = presetRange(preset, weekStartsOn);

  switch (variant) {
    case "range":
      return range;

    case "multiple": {
      const days: Date[] = [];
      const MAX_EXPANDED_DAYS = 31;
      let cursor = range.from;
      while (days.length < MAX_EXPANDED_DAYS && (!range.to || cursor <= range.to)) {
        days.push(cursor);
        if (!range.to) break;
        cursor = addDays(cursor, 1);
      }
      return days;
    }

    case "time":
      // Time has no calendar meaning; presets resolve to the current wall clock.
      return formatHm(new Date().getHours(), new Date().getMinutes());

    case "single":
    case "datetime":
    default:
      return range.from;
  }
}
